<template>
  <div class="page-pay-waiting">
    <div class="pay-waiting-content">
      <loading></loading>
      <h2 class="title">正在跳转</h2>
      <ol class="desc">
        <li>如果已完成支付，请点击『支付完成』;</li>
        <li>如果未打开微信客户端或未完成支付，请点击『重新支付』返回再次尝试。</li>
      </ol>
      <div class="action">
        <ym-button class="btn"
                   plain
                   size="large"
                   :disabled="disabledPayAgainBtn"
                   @click="handleClickPayAgain"
        >
          重新支付
        </ym-button>
        <ym-button class="btn"
                   plain
                   type="primary"
                   size="large"
                   :disabled="disabledPaidBtn"
                   @click="handleClickPaid"
        >
          支付成功
        </ym-button>
      </div>
    </div>
  </div>
</template>

<script>
  import { Button } from '@ymba/ym-ui';
  import wx from 'weixin-js-sdk';
  import Loading from '@/components/Loading';
  import { getOrderStatus, getGroupBuyNotify, getWeChatPayUrl } from '@/services/order';
  import {
    locationHref,
    getProductPath,
    isAppWebView,
    setPaidRefreshDetail,
    isMiniWebView,
  } from '@/utils';
  import {
    MAX_POLL,
    JOIN_GROUP_BUY_TEAM_TARGET_TYPE,
    CREATE_GROUP_BUY_TEAM_TARGET_TYPE,
  } from '@/utils/constant';

  export default {
    name: 'PayWaiting',
    metaInfo: {
      title: '正在跳转',
    },
    components: {
      Loading,
      'ym-button': Button,
    },
    data() {
      return {
        pollCount: 0,
        duration: 1000,
        status: '',
        targetType: '',
        targetId: 0,
        disabledPayAgainBtn: false,
        disabledPaidBtn: false,
      };
    },
    computed: {
      sn() {
        return this.$route.query.sn;
      },
    },
    methods: {
      getNotifyPath(type, id, sn) {
        switch (type) {
          case JOIN_GROUP_BUY_TEAM_TARGET_TYPE:
            return { name: 'GroupBuyTeamDetail', params: { id } };
          default:
            return { name: 'PayNotify', params: { sn }, query: { type } };
        }
      },
      handleWebviewSuccess(targetType, targetId) {
        const canNotify = window.joinProductSuccess && typeof window.joinProductSuccess === 'function';
        if (canNotify) {
          window.joinProductSuccess({ targetId, targetType });
        }
      },
      async handlePaidSuccess() {
        const { targetId, targetType, sn } = this;
        setPaidRefreshDetail(targetType, targetId);
        // 如果在Webview 中，则通知App 端更新
        if (isAppWebView()) {
          this.handleWebviewSuccess(targetType, targetId);
        }
        let type = targetType;
        let id = targetId;
        if (targetType === CREATE_GROUP_BUY_TEAM_TARGET_TYPE) {
          const data = await getGroupBuyNotify(sn);
          const { id: teamId } = data.team;
          type = JOIN_GROUP_BUY_TEAM_TARGET_TYPE;
          id = teamId;
        }
        const route = this.getNotifyPath(type, id, sn);
        this.$router.replace(route);
      },
      getOrderInfo(sn, type = 'poll') {
        return new Promise(async (resolve, reject) => {
          if (this.pollCount >= MAX_POLL) {
            this.$dialog.alert({
              message: '订单正在处理中，请稍等片刻',
              confirmButtonText: '好的',
            }).then(() => {
              this.showMyOrderDetail(sn);
            });
            return;
          }
          this.pollCount += 1;
          try {
            let t;
            const data = await getOrderStatus(sn, type);
            this.targetId = data.targetId;
            this.targetType = data.targetType;
            this.status = data.status;
            resolve();
            if (data.status === 'paid') {
              if (t) {
                clearTimeout(t);
              }
              this.handlePaidSuccess();
              return;
            }
            if (t == null && type === 'poll') {
              t = setTimeout(() => {
                this.getOrderInfo(sn);
              }, this.duration);
            }
          } catch (e) {
            reject();
          }
        });
      },
      showMyOrderDetail(sn) {
        const route = { name: 'OrderDetail', params: { sn } };
        this.$router.push(route);
      },
      async handleClickPayAgain() {
        if (this.disabledPayAgainBtn) return;
        this.disabledPayAgainBtn = true;

        if (isMiniWebView()) {
          wx.miniProgram.navigateTo({ url: `/pages/pay/index?sn=${this.sn}` });
          return;
        }

        const data = await getWeChatPayUrl(this.sn, 'payWaiting');
        this.disabledPayAgainBtn = false;
        const { weChatUrl } = data;
        locationHref(weChatUrl);
      },
      async handleClickPaid() {
        if (this.disabledPaidBtn) return;
        this.disabledPaidBtn = true;

        await this.getOrderInfo(this.sn, 'click');
        this.disabledPaidBtn = false;
      },
      addRouteBackPath() {
        const { targetType, targetId, sn } = this;
        let back = { name: 'OrderDetail', params: { sn } };
        if (targetType !== '') {
          back = getProductPath(targetType, targetId);
        }
        this.$route.meta.back = back;
      },
    },
    async created() {
      // 默认返回订单详情
      this.addRouteBackPath();
      await this.getOrderInfo(this.sn);
      // 请求response 后重置返回链接至产品详情
      this.addRouteBackPath();
    },
    beforeRouteUpdate(to, from, next) {
      const { sn } = to.query;
      this.getOrderInfo(sn);
      next();
    },
  };
</script>

<style scoped lang="less">
  @import '~@/assets/styles/var.less';

  .pay-waiting-content {
    padding: (@size-medium * 2 + 100px) @size-medium (@size-medium * 2);
  }

  .header {
    position: absolute;
    top: 0;
    width: 100%;
    height: 88px;
    padding: 0 @size-base;
    border-bottom: @border-width-base solid @color-gray-lighter;
    box-sizing: border-box;
    font-size: @font-size-large;
    font-weight: @font-weight-primary;
    color: @color-black;
    text-align: center;
    display: flex;
    align-items: center;

    .icon-back {
      font-size: @font-size-medium;
    }

    span {
      padding-right: @size-small;
      flex: 1;
    }
  }

  .title {
    text-align: center;
    font-size: @font-size-medium;
    margin: (@size-base * 2) auto;
  }

  .desc {
    margin: 0 @size-base;
    color: @color-text-lighter;
    line-height: 1.5;
    padding-left: @size-base;

    li {
      margin-bottom: @size-base;
    }
  }

  .action {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: @size-medium -@size-base;
  }

  .btn {
    font-size: @font-size-medium;
    margin: 0 @size-base;
  }
</style>
